import { useEffect, useMemo, useState } from "react";
import { Alert, Button, Form } from "react-bootstrap";
import { ExcelRenderer } from "react-excel-renderer";
import { v4 as uuid } from "uuid";
import { utils, writeFileXLSX } from "xlsx";
import AuthService from "../services/auth.service";
import DataService from "../services/data.service";
import { formatCurrency } from "../common/reporting";

const ProcessTransactions = () => {
  const [rows, setRows] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [fileValid, setFileValid] = useState(true);
  const [clientData, setClientData] = useState(null);
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState("");
  const [batchNo, setBatchNo] = useState("");
  const [displayMessage, setDisplayMessage] = useState("");
  const [messageVariant, setMessageVariant] = useState("info");
  const [isProcessing, setIsProcessing] = useState(false);

  const user = AuthService.getCurrentUser();
  const clientId = user && user.clients && user.clients[0] && user.clients[0].companyCode;
  const clientPrimaryKeyId = user && user.clients && user.clients[0] && user.clients[0].id;

  useEffect(() => {
    if (!clientId) {
      return;
    }

    DataService.getClient(clientId).then((response) => {
      setClientData(response.data);
    });
  }, [clientId]);

  useEffect(() => {
    if (!clientPrimaryKeyId) {
      return;
    }

    DataService.fetchProjects(clientPrimaryKeyId).then((response) => {
      setProjects(response.data);

      if (response.data.length === 0) {
        setProjectId("1000");
      } else {
        setProjectId(String(response.data[0].id));
      }
    });
  }, [clientPrimaryKeyId]);

  const exportToCSV = () => {
    const exportData = [
      {
        Name: "Sample Client",
        PhoneNumber: 254723000000,
        Amount: 10,
        Remarks: "This is sample data",
      },
    ];

    const worksheet = utils.json_to_sheet(exportData);
    const workbook = utils.book_new();
    utils.book_append_sheet(workbook, worksheet, "Template");
    writeFileXLSX(workbook, "payme-template.xlsx");
  };

  const fileHandler = (event) => {
    const fileObj = event.target.files && event.target.files[0];

    if (!fileObj) {
      return;
    }

    ExcelRenderer(fileObj, (error, response) => {
      if (error) {
        setFileValid(false);
        setTableData([]);
        setRows([]);
        setDisplayMessage("We could not read that spreadsheet. Please upload a valid Excel file.");
        setMessageVariant("danger");
        return;
      }

      if (
        JSON.stringify(response.rows[0]) !==
        JSON.stringify(["Name", "PhoneNumber", "Amount", "Remarks"])
      ) {
        setFileValid(false);
        setTableData([]);
        setRows([]);
        setDisplayMessage(
          "The spreadsheet format is invalid. Use the PayMe template with Name, PhoneNumber, Amount, and Remarks columns."
        );
        setMessageVariant("danger");
        return;
      }

      const rowsToShow = response.rows.filter(
        (_, index) => index > 0 && response.rows[index].length > 0
      );
      const transformedRows = rowsToShow.map((row) => ({
        customerName: row[0],
        phoneNumber: row[1],
        amount: row[2],
        description: row[3],
        status: "Ready for upload",
        statusMessage: "Awaiting processing",
        uniqueId: "—",
      }));

      setFileValid(true);
      setRows(rowsToShow);
      setTableData(transformedRows);
      setBatchNo("");
      setDisplayMessage(`${rowsToShow.length} transaction rows are ready for review.`);
      setMessageVariant("info");
    });
  };

  const handleTransactionsProcess = () => {
    if (batchNo !== "") {
      setDisplayMessage(
        "This file has already been uploaded. Review the transactions report for the processing outcome."
      );
      setMessageVariant("warning");
      return;
    }

    if (!rows.length) {
      setDisplayMessage("Upload a valid spreadsheet before processing transactions.");
      setMessageVariant("warning");
      return;
    }

    setIsProcessing(true);

    const filteredRows = rows.filter((row) => row.length > 0);
    const transactions = filteredRows.map((row) => {
      const uniqueId = `trans_${clientId}_${uuid().slice(0, 8)}`;

      return {
        uniqueId,
        phoneNumber: row[1].toString(),
        customerName: row[0],
        description: row[3],
        amount: parseFloat(row[2]),
      };
    });

    const batchIdentifier = `batch_${clientId}_${uuid().slice(0, 8)}`;

    DataService.processTransactions({
      batchIdentifier,
      companyCode: clientId,
      transactions,
      projectId,
    }).then(
      (response) => {
        setBatchNo(batchIdentifier);
        setTableData(response.data.transactions);
        setDisplayMessage("The payment batch was uploaded successfully.");
        setMessageVariant("success");
        setIsProcessing(false);
      },
      (error) => {
        const responseMessage =
          (error.response && error.response.data && error.response.data.message) ||
          error.message ||
          error.toString();
        setDisplayMessage(responseMessage);
        setMessageVariant("danger");
        setIsProcessing(false);
      }
    );
  };

  const previewSummary = useMemo(() => {
    return {
      count: tableData.length,
      total: tableData.reduce((total, row) => total + Number(row.amount || 0), 0),
      selectedProject:
        projects.find((project) => String(project.id) === String(projectId)) || null,
    };
  }, [projectId, projects, tableData]);

  if (!clientId) {
    return (
      <section className="empty-state empty-state--large">
        <span className="bi bi-building-exclamation" aria-hidden="true" />
        <h4>No client profile linked</h4>
        <p>Contact an administrator before processing transactions in this portal.</p>
      </section>
    );
  }

  return (
    <div className="portal-page-stack">
      <section className="page-grid page-grid--three">
        <article className="metric-card metric-card--highlight">
          <p className="metric-card__label">Client balance</p>
          <h4>{formatCurrency(clientData && clientData.balance)}</h4>
          <span>{clientData ? clientData.clientName : "Loading client profile..."}</span>
        </article>
        <article className="metric-card">
          <p className="metric-card__label">Rows prepared</p>
          <h4>{previewSummary.count}</h4>
          <span>Validated transaction instructions currently staged in the portal.</span>
        </article>
        <article className="metric-card">
          <p className="metric-card__label">Batch total</p>
          <h4>{formatCurrency(previewSummary.total)}</h4>
          <span>Estimated disbursement total for the uploaded batch preview.</span>
        </article>
      </section>

      <section className="page-grid page-grid--two">
        <article className="content-card content-card--padded">
          <div className="section-heading">
            <div>
              <p className="section-heading__eyebrow">Guided upload</p>
              <h4>Prepare a disbursement batch</h4>
            </div>
            <Button variant="outline-primary" onClick={exportToCSV}>
              Download template
            </Button>
          </div>
          <p className="content-card__supporting">
            Upload an Excel file using the standard PayMe template. Review the preview before
            sending transactions to processing.
          </p>
          <div className="portal-form-grid">
            <Form.Group controlId="transactionProject">
              <Form.Label>Project</Form.Label>
              <Form.Control
                as="select"
                value={projectId}
                onChange={(event) => setProjectId(event.target.value)}
              >
                {projects.map((project) => (
                  <option value={project.id} key={project.id}>
                    {project.projectName}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
            <Form.Group controlId="transactionUpload">
              <Form.Label>Batch file</Form.Label>
              <Form.Control type="file" accept=".xls,.xlsx" onChange={fileHandler} />
            </Form.Group>
          </div>
          <div className="button-row mt-4">
            <Button variant="primary" onClick={handleTransactionsProcess} disabled={isProcessing}>
              {isProcessing ? "Processing..." : "Process transactions"}
            </Button>
            {batchNo ? <span className="portal-pill portal-pill--outline">{batchNo}</span> : null}
          </div>
          {displayMessage ? (
            <Alert variant={messageVariant} className="mt-4 mb-0">
              {displayMessage}
            </Alert>
          ) : null}
        </article>

        <article className="content-card content-card--padded content-card--dark">
          <div className="section-heading">
            <div>
              <p className="section-heading__eyebrow">Batch controls</p>
              <h4>Operational checklist</h4>
            </div>
          </div>
          <ul className="signal-list signal-list--light mb-4">
            <li>Use the standard template to reduce formatting exceptions.</li>
            <li>Confirm project allocation before processing the batch.</li>
            <li>Review the preview table for amount and phone number anomalies.</li>
          </ul>
          <div className="detail-grid detail-grid--single detail-grid--light">
            <div>
              <span>Selected project</span>
              <strong>
                {previewSummary.selectedProject
                  ? previewSummary.selectedProject.projectName
                  : "Default project assignment"}
              </strong>
            </div>
            <div>
              <span>Client code</span>
              <strong>{clientId}</strong>
            </div>
            <div>
              <span>Batch reference</span>
              <strong>{batchNo || "Generated on submit"}</strong>
            </div>
          </div>
        </article>
      </section>

      <section className="content-card content-card--padded">
        <div className="section-heading">
          <div>
            <p className="section-heading__eyebrow">Preview and results</p>
            <h4>Transaction table</h4>
          </div>
        </div>
        {fileValid ? (
          tableData.length ? (
            <div className="table-responsive portal-table-wrap">
              <table className="table portal-table align-middle mb-0">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Phone number</th>
                    <th>Amount</th>
                    <th>Remarks</th>
                    <th>Status</th>
                    <th>Status message</th>
                    <th>Unique ID</th>
                  </tr>
                </thead>
                <tbody>
                  {tableData.map((row, index) => (
                    <tr key={`${row.phoneNumber}-${index}`}>
                      <td>{row.customerName}</td>
                      <td>{row.phoneNumber}</td>
                      <td>{formatCurrency(row.amount)}</td>
                      <td>{row.description}</td>
                      <td>{row.status || "Ready"}</td>
                      <td>{row.statusMessage || "Awaiting processing"}</td>
                      <td>{row.uniqueId || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="empty-state">
              <span className="bi bi-file-earmark-spreadsheet" aria-hidden="true" />
              <h5>No batch uploaded yet</h5>
              <p>Upload a spreadsheet to preview transactions before you process them.</p>
            </div>
          )
        ) : (
          <div className="empty-state empty-state--compact">
            <span className="bi bi-exclamation-triangle" aria-hidden="true" />
            <h5>Invalid spreadsheet format</h5>
            <p>Use the template so the portal can validate transaction rows correctly.</p>
          </div>
        )}
      </section>
    </div>
  );
};

export default ProcessTransactions;
